import { getSupabase } from './index'

export async function sharedAuthLogin(email: string, password: string) {
  const supabase = getSupabase()
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })

  if (error) {
    return { success: false, error: error.message }
  }

  if (data.user && data.session) {
    return {
      success: true,
      user: { id: data.user.id, email: data.user.email || '' },
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token
    }
  }

  return { success: false, error: 'Sign in failed' }
}

export async function sharedAuthSignup(email: string, password: string, username?: string, avatarUrl?: string) {
  const supabase = getSupabase()

  const metadata: Record<string, string> = {}
  if (username) {
    metadata.username = username
    metadata.display_name = username
  }
  if (avatarUrl) {
    metadata.avatar_url = avatarUrl
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: Object.keys(metadata).length > 0 ? metadata : undefined
    }
  })

  if (error) {
    return { success: false, error: error.message }
  }

  if (data.user) {
    if (data.session) {
      return {
        success: true,
        user: { id: data.user.id, email: data.user.email || '' },
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token
      }
    }
    return { success: true, needsConfirmation: true }
  }

  return { success: true, needsConfirmation: true }
}

export async function sharedAuthOAuth(provider: 'google' | 'github', redirectTo?: string) {
  const supabase = getSupabase()
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectTo ? `${redirectTo}/auth/callback` : `${window.location.origin}/auth/callback`
    }
  })

  if (error) {
    return { success: false, error: error.message }
  }

  if (data.url) {
    return { success: true, url: data.url }
  }

  return { success: false, error: 'Failed to initiate OAuth' }
}

export async function sharedAuthGetSession() {
  const supabase = getSupabase()
  
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error || !session) {
    return { authenticated: false }
  }

  const { data: { user } } = await supabase.auth.getUser()

  if (user) {
    return {
      authenticated: true,
      user: { id: user.id, email: user.email || '' },
      access_token: session.access_token,
      refresh_token: session.refresh_token || ''
    }
  }

  return { authenticated: false }
}

export async function sharedAuthRefresh() {
  const supabase = getSupabase()
  
  const { data, error } = await supabase.auth.refreshSession()

  if (error || !data.session) {
    return { success: false, error: error?.message || 'Failed to refresh session' }
  }

  return {
    success: true,
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token || ''
  }
}

export async function sharedAuthLogout() {
  const supabase = getSupabase()
  
  await supabase.auth.signOut()
  return { success: true }
}

export async function handleAuthCallback(_sessionId: string, _token: string, _refreshToken: string) {
  return { success: true }
}

export function setAuthWorkerUrl(_url: string) {
  // No longer needed - auth worker removed
}

export function getAuthWorkerUrl(): string {
  // No longer needed - auth worker removed
  return ''
}

export function setCachedSession(_session: { session_id: string; access_token: string; refresh_token: string; user: { id: string; email: string } } | null) {
  // No longer needed - Supabase handles session storage
}

export function getCachedSession() {
  // No longer needed - Supabase handles session storage
  return null
}

export async function fetchFromAuthWorker<T>(_endpoint: string, _options: RequestInit = {}): Promise<T> {
  throw new Error('Auth worker has been removed. Use Supabase auth directly.')
}
